import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import "./admin-data-grid.css";

export type AdminDataGridColumn<TRow> = {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: "left" | "center" | "right";
  render: (row: TRow) => ReactNode;
  mobilePriority?: "primary" | "secondary" | "hidden";
};

type AdminDataGridProps<TRow> = {
  columns: Array<AdminDataGridColumn<TRow>>;
  rows: readonly TRow[];
  getRowId: (row: TRow) => string;
  getRowLabel: (row: TRow) => string;
  getDetailHref?: (row: TRow) => string;
  selectedIds: ReadonlySet<string>;
  sortKey: string;
  sortOrder: "asc" | "desc";
  loading?: boolean;
  errorMessage?: string | null;
  emptyMessage: string;
  onRetry?: () => void;
  onToggleRow: (rowId: string) => void;
  onToggleCurrentPage: () => void;
  onSort: (columnKey: string) => void;
  onOpenRow?: (row: TRow) => void;
};

function renderSortMark(active: boolean, order: "asc" | "desc"): string {
  if (!active) return "";
  return order === "asc" ? " ↑" : " ↓";
}

export function AdminDataGrid<TRow>({
  columns,
  rows,
  getRowId,
  getRowLabel,
  getDetailHref,
  selectedIds,
  sortKey,
  sortOrder,
  loading = false,
  errorMessage = null,
  emptyMessage,
  onRetry,
  onToggleRow,
  onToggleCurrentPage,
  onSort,
  onOpenRow
}: AdminDataGridProps<TRow>) {
  const currentPageIds = rows.map((row) => getRowId(row));
  const selectedOnPage = currentPageIds.filter((rowId) => selectedIds.has(rowId)).length;
  const allCurrentPageSelected = currentPageIds.length > 0 && selectedOnPage === currentPageIds.length;
  const primaryColumns = columns.filter((column) => column.mobilePriority === "primary");
  const secondaryColumns = columns.filter((column) => column.mobilePriority !== "primary" && column.mobilePriority !== "hidden");

  if (loading) {
    return <div className="admin-data-grid__state">読み込んでいます...</div>;
  }

  if (errorMessage) {
    return (
      <div className="admin-data-grid__state admin-data-grid__state--error">
        <p>{errorMessage}</p>
        {onRetry ? <button type="button" onClick={onRetry}>再読み込み</button> : null}
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className="admin-data-grid__state">{emptyMessage}</div>;
  }

  return (
    <div className="admin-data-grid">
      <div className="admin-data-grid__table-wrap" role="region" aria-label="一覧テーブル" tabIndex={0}>
        <table className="admin-data-grid__table">
          <thead>
            <tr>
              <th className="admin-data-grid__checkbox-cell" scope="col">
                <input
                  type="checkbox"
                  aria-label="現在のページを選択"
                  checked={allCurrentPageSelected}
                  ref={(element) => {
                    if (element) element.indeterminate = selectedOnPage > 0 && !allCurrentPageSelected;
                  }}
                  onChange={onToggleCurrentPage}
                />
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={{ width: column.width, textAlign: column.align ?? "left" }}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      className="admin-data-grid__sort-button"
                      onClick={() => onSort(column.key)}
                    >
                      {column.header}
                      {renderSortMark(sortKey === column.key, sortOrder)}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
              <th className="admin-data-grid__detail-cell" scope="col">詳細</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rowId = getRowId(row);
              const selected = selectedIds.has(rowId);

              return (
                <tr
                  key={rowId}
                  className="admin-data-grid__row"
                >
                  <td className="admin-data-grid__checkbox-cell">
                    <input
                      type="checkbox"
                      aria-label={`${getRowLabel(row)}を選択`}
                      checked={selected}
                      onChange={() => onToggleRow(rowId)}
                    />
                  </td>
                  {columns.map((column) => (
                    <td key={column.key} style={{ textAlign: column.align ?? "left" }}>
                      {column.render(row)}
                    </td>
                  ))}
                  <td className="admin-data-grid__detail-cell">
                    {getDetailHref ? (
                      <Link className="admin-data-grid__detail-link" to={getDetailHref(row)} aria-label={`${getRowLabel(row)}の詳細を開く`}>
                        ›
                      </Link>
                    ) : onOpenRow ? (
                      <button type="button" className="admin-data-grid__detail-link" onClick={() => onOpenRow(row)} aria-label={`${getRowLabel(row)}の詳細を開く`}>
                        ›
                      </button>
                    ) : (
                      <span aria-hidden="true">›</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="admin-data-grid__cards">
        {rows.map((row) => {
          const rowId = getRowId(row);

          return (
            <article key={rowId} className="admin-data-grid__card">
              <div className="admin-data-grid__card-select">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(rowId)}
                    onChange={() => onToggleRow(rowId)}
                  />
                  <span>選択</span>
                </label>
              </div>
              <div className="admin-data-grid__card-open">
                <div className="admin-data-grid__card-primary">
                  {primaryColumns.map((column) => (
                    <div key={column.key}>{column.render(row)}</div>
                  ))}
                </div>
                <div className="admin-data-grid__card-secondary">
                  {secondaryColumns.map((column) => (
                    <div key={column.key}>
                      <span>{column.header}: </span>
                      {column.render(row)}
                    </div>
                  ))}
                </div>
                {getDetailHref ? (
                  <Link className="admin-data-grid__card-chevron" to={getDetailHref(row)} aria-label={`${getRowLabel(row)}の詳細を開く`}>
                    ›
                  </Link>
                ) : onOpenRow ? (
                  <button type="button" className="admin-data-grid__card-chevron" onClick={() => onOpenRow(row)} aria-label={`${getRowLabel(row)}の詳細を開く`}>
                    ›
                  </button>
                ) : (
                  <span className="admin-data-grid__card-chevron" aria-hidden="true">›</span>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
